import { useQuery } from "@tanstack/react-query";
import { Building, DollarSign, ExternalLink, Shield, AlertTriangle } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FacilitySecuritiesTable } from "./facility-securities-table";
import {
  buildUnlinkedGuarantorWarning,
  type UnlinkedGuarantor,
} from "../securities/guarantor-wizard";
import type { Facility, Security } from "@shared/schema";

interface FacilitySecuritySummary {
  facilityId: string;
  totalSecurityValue: number;
  lvr: number;
  securitiesCount: number;
  encumbrancesCount: number;
  encumbrancesTotal: number;
}

interface FacilitySecuritiesPanelProps {
  facilityId: string;
  dealId?: string;
  facilityLimit?: string | null;
  facilityName?: string;
  clientGroupId?: string;
}

interface LinkedSecurity extends Security {
  linkId: string;
  isFirstRanking: boolean | null;
  securityPosition: string | null;
  securedAmount: string | null;
  includeInLvr: boolean | null;
  lvrContributionPercentage: string | null;
  facilityCount?: number | null;
}

export function FacilitySecuritiesPanel({
  facilityId,
  dealId,
  facilityLimit: _facilityLimit,
  facilityName: _facilityName,
  clientGroupId: _clientGroupId,
}: FacilitySecuritiesPanelProps) {
  const [, navigate] = useLocation();

  const { data: securities = [], isLoading: isLoadingSecurities } = useQuery<LinkedSecurity[]>({
    queryKey: [`/api/crm/facilities/${facilityId}/securities`],
    enabled: !!facilityId,
    staleTime: 500,
    refetchInterval: 1000,
  });

  const { data: summary } = useQuery<FacilitySecuritySummary>({
    queryKey: [`/api/crm/facilities/${facilityId}/securities/summary`],
    enabled: !!facilityId,
    staleTime: 500,
    refetchInterval: 1000,
  });

  const { data: facilityMeta } = useQuery<Facility>({
    queryKey: ["/api/crm/facilities", facilityId],
    enabled: !!facilityId && !dealId,
  });

  const { data: unlinkedGuarantors = [] } = useQuery<UnlinkedGuarantor[]>({
    queryKey: [`/api/crm/facilities/${facilityId}/unlinked-guarantors`],
    enabled: !!facilityId,
  });

  const resolvedDealId = dealId ?? facilityMeta?.dealId ?? null;
  const guarantorWarning =
    unlinkedGuarantors.length > 0 ? buildUnlinkedGuarantorWarning(unlinkedGuarantors) : null;

  const navigateToDealRegister = () => {
    if (!resolvedDealId) return;
    navigate(`/deals/${resolvedDealId}#securities`);
  };

  const formatCurrency = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatPercentage = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "-";
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="space-y-4">
      {guarantorWarning && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{guarantorWarning.title}</AlertTitle>
          <AlertDescription>
            <div className="space-y-3">
              <p>{guarantorWarning.description}</p>
              {resolvedDealId && (
                <Button size="sm" variant="secondary" onClick={navigateToDealRegister}>
                  Open Deal Register
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Alert>
        <Shield className="h-4 w-4" />
        <AlertTitle>Central Register View</AlertTitle>
        <AlertDescription>
          This tab is a filtered view of the central deal-level securities register.
          {resolvedDealId ? " Manage canonical securities from the deal register." : ""}
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="securities-summary-cards">
        <Card data-testid="total-value-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Total Value</p>
                <p className="text-sm font-semibold" data-testid="total-value-amount">
                  {formatCurrency(summary?.totalSecurityValue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="lvr-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">LVR</p>
                <p
                  className={`text-sm font-semibold ${
                    summary?.lvr && summary.lvr > 80
                      ? "text-orange-600"
                      : summary?.lvr && summary.lvr > 70
                        ? "text-yellow-600"
                        : "text-green-600"
                  }`}
                  data-testid="lvr-percentage"
                >
                  {formatPercentage(summary?.lvr)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="securities-count-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Securities</p>
                <p className="text-sm font-semibold" data-testid="securities-count">
                  {summary?.securitiesCount ?? securities.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="encumbrances-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Encumbrances</p>
                <p className="text-sm font-semibold" data-testid="encumbrances-count">
                  {summary?.encumbrancesCount ?? 0}
                  {summary?.encumbrancesTotal ? ` (${formatCurrency(summary.encumbrancesTotal)})` : ""}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Linked Securities</h3>
        {resolvedDealId && (
          <Button size="sm" variant="outline" onClick={navigateToDealRegister}>
            <ExternalLink className="mr-1 h-3 w-3" />
            Manage in Deal Register
          </Button>
        )}
      </div>

      <FacilitySecuritiesTable
        facilityId={facilityId}
        securities={securities}
        isLoading={isLoadingSecurities}
        onRowClick={() => navigateToDealRegister()}
        onViewInDealRegister={() => navigateToDealRegister()}
      />
    </div>
  );
}
